import React, { useContext, useState } from "react";

import { Button } from "@components/buttons";
import { GROUPED_BETTING_TYPES } from "@data-types/offer/event-betting-type/BettingTypeGroups";
import { TStatisticsBettingData } from "@data-types/offer/event-betting-type/StatisticsBettingData";
import { TStatisticsBettingDataSetterFn } from "@data-types/offer/event-betting-type/StatisticsBettingDataSetterFn";
import BettingTypeStatisticsButton from "@features/offer/mobile/shared/button/BettingTypeStatisticsButton";
import { BettingTypeStatistics } from "@features/offer/mobile/statistics";
import { EventContext } from "@gp/components";
import { BettingType as TBettingType } from "@gp/models";
import { getSortedEventKeysBySpecifierValue } from "@gp/offer";
import { NBATeamNameComparer, NameProvider, Sorter } from "@gp/utility";
import { BetBuilderModeContext } from "@state/context/bet-builder/BetBuilderModeContext";
import { FavoritesStoreContext } from "@state/context/favorites/FavoritesStoreContext";
import { OfferStore } from "@state/context/offer/OfferContext";
import { useOfferContext } from "@state/hooks/offer/useOfferContext";
import classNames from "classnames";
import { observer } from "mobx-react";

import { BettingTypeDescription } from "./BettingTypeDescription";
import BettingTypeRow from "./BettingTypeRow";

const nameProvider = new NameProvider();

export const BettingType = observer(function BettingType(props: {
	isFavorite?: boolean;
	bettingType: TBettingType;
}) {
	const { bettingType, isFavorite } = props;
	const [toggleState, setToggleState] =
		useState<TStatisticsBettingData | null>(null);

	const favoriteStore = useContext(FavoritesStoreContext);
	const event = useContext(EventContext);
	const isBetBuilder = useContext(BetBuilderModeContext);

	const store = useOfferContext<OfferStore.EventViewStore>();

	if (store.eventKeysMap == null || store.eventKeysMap.size === 0) {
		return null;
	}

	const eventKeys = store.eventKeysMap.get(event.id);

	if (eventKeys == null || eventKeys.size === 0) {
		return null;
	}

	let bettingTypeKeys = Array.from(eventKeys.values()).filter(
		(key) => key.bettingType.abrv === bettingType.abrv
	);
	if (isBetBuilder) {
		bettingTypeKeys = bettingTypeKeys.filter((bt) => {
			return bt.betBuilder;
		});
		if (bettingTypeKeys.length == 0) {
			return null;
		}
	}
	let eventKeysBySpecifierValue =
		getSortedEventKeysBySpecifierValue(bettingTypeKeys);

	if (isFavorite && bettingTypeKeys[0]?.specifier) {
		eventKeysBySpecifierValue = eventKeysBySpecifierValue.filter(
			(marginal) => {
				return favoriteStore.isUserFavoriteBettingType(
					marginal.bettingTypeId,
					marginal.specifier || null
				);
			}
		);
	}

	const teamOneName =
		store.lookupsStore.teams.get(event.teamOneId || "")?.name || "";

	const isGrouped =
		[
			"player-total-assists",
			"player-total-rebounds",
			"player-total-3-point-field-goals",
			"player-total-points",
			// "1st-half-over-under-corners",
			// "tip-1x2-and-over-under",
		].includes(bettingType.abrv || "") ||
		GROUPED_BETTING_TYPES.includes(bettingType.abrv || "");

	if (teamOneName != null && isGrouped) {
		eventKeysBySpecifierValue.sort(
			Sorter.sort((a, b) =>
				NBATeamNameComparer(a, b, event, store.lookupsStore.teams)
			)
		);
	}

	const onClick: TStatisticsBettingDataSetterFn = (action, data) => {
		if (toggleState?.action == action) {
			setToggleState(null);
		} else {
			setToggleState({ action: action, data: data });
		}
	};

	return (
		<div
			className={classNames("offer--additional__card", {
				"offer--single": !isGrouped,
				"offer--group": isGrouped,
				"is-inner": event.isOutright,
			})}
		>
			{isGrouped && (
				<div className="offer--additional__header">
					<div className="match__details__info">
						<span className="match__details__name">
							{bettingType.simpleName}
						</span>
					</div>

					<div className="offer--additional__actions">
						<BettingTypeStatisticsButton
							toggleState={toggleState}
							setToggleState={onClick}
							bettingType={bettingType}
							offerKey={eventKeysBySpecifierValue}
						/>
						<Button
							className={
								"btn--square btn--square--tny btn--ghost offer--additional__btn" /* +
								(toggleState?.action == "info"
									? " btn--secondary"
									: " btn--ghost") */
							}
							iconOnly
							iconClassName={
								"u-icon--sml" +
								(toggleState?.action == "info"
									? " u-icon--info-circle--bold"
									: " u-icon--info-circle")
							}
							onClick={() => onClick("info", null)}
						/>
					</div>
				</div>
			)}

			{isGrouped && toggleState?.action && (
				<React.Fragment>
					{toggleState?.action == "info" && (
						<div className="match__panel">
							<BettingTypeDescription
								className="stats-info"
								description={nameProvider.getName(
									bettingType.description,
									{}
								)}
								shouldDisplay={toggleState?.action == "info"}
							/>
						</div>
					)}

					<BettingTypeStatistics
						bettingTypeStats={toggleState}
						bettingTypeAbrv={bettingType.abrv}
					/>
				</React.Fragment>
			)}

			{eventKeysBySpecifierValue.map((key, index) => (
				<React.Fragment key={key.id}>
					<BettingTypeRow
						bettingType={bettingType}
						offerKey={key}
						isGroup={isGrouped}
						index={index}
						toggleState={toggleState}
						setToggleState={onClick}
					/>
				</React.Fragment>
			))}
		</div>
	);
});
